/**
 * Build responsive image attributes for a remote image URL.
 *
 * For Supabase Storage URLs, generates a `srcSet` using on-the-fly width
 * transforms (`?width=...&quality=...`) so phones download a ~480-800px
 * variant instead of the full uploaded resolution. Cached at the edge after
 * first hit. For non-Supabase URLs (or asset CDN URLs), the original URL is
 * returned unchanged.
 *
 * Example:
 *   const img = responsiveImg(url, "(min-width: 1024px) 50vw, 100vw");
 *   <img src={img.src} srcSet={img.srcSet} sizes={img.sizes} alt="..." />
 */
export type ResponsiveImg = {
  src: string;
  srcSet?: string;
  sizes?: string;
};

const DEFAULT_WIDTHS = [480, 800, 1200, 1600] as const;
const DEFAULT_QUALITY = 65;

function isSupabaseStorageUrl(url: string): boolean {
  // Supabase Storage public URLs look like
  // https://<project>.supabase.co/storage/v1/object/public/<bucket>/...
  return /\/storage\/v1\/(object|render\/image)\//.test(url);
}

/**
 * Switches a Supabase Storage `object` URL to the `render/image` transform
 * endpoint. The `object` endpoint serves the original; only `render/image`
 * honors `?width=` resizing.
 */
function toRenderUrl(url: string): string {
  return url.replace("/storage/v1/object/", "/storage/v1/render/image/");
}

export function responsiveImg(
  url: string | null | undefined,
  sizes = "(min-width: 1024px) 50vw, 100vw",
  widths: readonly number[] = DEFAULT_WIDTHS,
  quality = DEFAULT_QUALITY,
): ResponsiveImg {
  if (!url) return { src: "" };
  if (!isSupabaseStorageUrl(url)) return { src: url };

  const base = toRenderUrl(url);
  const sep = base.includes("?") ? "&" : "?";
  const srcSet = widths
    .map((w) => `${base}${sep}width=${w}&quality=${quality}&format=webp ${w}w`)
    .join(", ");
  // Default `src` targets a middle width so non-srcset browsers (very rare)
  // don't pull the full original.
  const fallbackWidth = widths[Math.min(2, widths.length - 1)];
  return {
    src: `${base}${sep}width=${fallbackWidth}&quality=${quality}&format=webp`,
    srcSet,
    sizes,
  };
}
